import { useParams, Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useProvider } from '@/hooks/useProviders'
import { ProviderLifecycleStatus, RiskLevel } from '@kfolks/types'
import { ArrowLeft, Loader2 } from 'lucide-react'

const LIFECYCLE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	[ProviderLifecycleStatus.ACTIVE]: 'default',
	[ProviderLifecycleStatus.PLANNED]: 'outline',
	[ProviderLifecycleStatus.POC]: 'secondary',
	[ProviderLifecycleStatus.TERMINATED]: 'destructive',
}

const RISK_COLORS: Record<string, string> = {
	[RiskLevel.HIGH]: 'bg-red-100 text-red-800',
	[RiskLevel.MEDIUM]: 'bg-yellow-100 text-yellow-800',
	[RiskLevel.LOW]: 'bg-green-100 text-green-800',
}

const DATA_CLASSIFICATION_LABELS: Record<string, string> = {
	pii: 'PII',
	financial_data: 'Financial',
	health_data: 'Health',
	intellectual_property: 'IP',
	public: 'Public',
	internal: 'Internal',
	confidential: 'Confidential',
	restricted: 'Restricted',
}

export function ProviderDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: provider, isLoading, error } = useProvider(id ?? '')

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-24">
				<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
			</div>
		)
	}

	if (error || !provider) {
		return (
			<div className="space-y-4">
				<Link to="/providers">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="h-4 w-4 mr-1" /> Back to Providers
					</Button>
				</Link>
				<div className="rounded-lg bg-red-50 p-6 text-center text-red-700">Provider not found</div>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Link to="/providers">
					<Button variant="ghost" size="sm">
						<ArrowLeft className="h-4 w-4 mr-1" /> Providers
					</Button>
				</Link>
				<div>
					<h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
					<p className="text-sm font-mono text-gray-500">{provider.code}</p>
				</div>
				<div className="ml-auto flex gap-2">
					<Badge variant={LIFECYCLE_BADGE_VARIANT[provider.lifecycleStatus] ?? 'secondary'}>
						{provider.lifecycleStatus}
					</Badge>
				</div>
			</div>

			{provider.description && <p className="text-sm text-gray-600">{provider.description}</p>}

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Risk Level</h3>
						<span
							className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${RISK_COLORS[provider.riskLevel] ?? 'bg-gray-100'}`}
						>
							{provider.riskLevel}
						</span>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Business Impact</h3>
						<span className="text-sm font-medium text-gray-900">
							{provider.businessImpact === 'critical' ? 'Critical' : 'Non-critical'}
						</span>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-1">Owner</h3>
						<span className="text-sm text-gray-900">
							{provider.ownerId ? provider.ownerId.substring(0, 8) + '...' : 'Not assigned'}
						</span>
					</CardContent>
				</Card>
			</div>

			{provider.dataClassification && provider.dataClassification.length > 0 && (
				<Card>
					<CardContent className="p-4">
						<h3 className="text-xs font-medium text-gray-500 mb-2">Data Classification</h3>
						<div className="flex flex-wrap gap-2">
							{provider.dataClassification.map((dc) => (
								<span key={dc} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700">
									{DATA_CLASSIFICATION_LABELS[dc] ?? dc}
								</span>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardContent className="p-4">
					<h3 className="text-xs font-medium text-gray-500 mb-1">Status</h3>
					<span className={`text-sm font-medium ${provider.isActive ? 'text-green-700' : 'text-red-700'}`}>
						{provider.isActive ? 'Active' : 'Inactive'}
					</span>
				</CardContent>
			</Card>
		</div>
	)
}
